/**
 * DocumentVaultPage
 * 
 * Main document vault interface with folder tree, document grid/list, and actions
 */

import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Upload, FolderPlus, Grid, List, Search, 
  SlidersHorizontal, RefreshCw
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { API_BASE_URL } from '@/core/lib/api-client';

// Feature imports
import { useDocumentsStore } from '../stores/documentsStore';
import { useDocuments, useDocumentStats } from '../hooks/useDocuments';
import { useFolders, useCreateFolder, useMoveFolder, useDeleteFolder } from '../hooks/useFolders';
import { useBulkMoveDocuments, useBulkDeleteDocuments, useBulkDownloadDocuments } from '../hooks/useDocuments';
import { Document, DocumentFolder, ViewMode } from '../types/documentsTypes';

// Components
import { FolderTree } from '../components/FolderTree';
import { FolderBreadcrumb } from '../components/FolderBreadcrumb';
import { DocumentGrid } from '../components/DocumentGrid';
import { DocumentList } from '../components/DocumentList';
import { DocumentFilters } from '../components/DocumentFilters';
import { DocumentUploader } from '../components/DocumentUploader';
import { DocumentViewer } from '../components/DocumentViewer';
import { FolderCreateModal, CreateFolderData } from '../components/FolderCreateModal';
import { DocumentMoveModal } from '../components/DocumentMoveModal';
import { ShareWithAttorneyModal } from '@/features/attorney/components/ShareWithAttorneyModal';
import { BulkActionBar } from '../components/BulkActionBar';

// ============================================================================
// Component
// ============================================================================

export const DocumentVaultPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // URL state
  const currentFolderId = searchParams.get('folder') || null;
  
  // Store state
  const {
    viewMode,
    setViewMode,
    selectedDocumentIds,
    clearSelection,
    filters,
    setFilters,
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
  } = useDocumentsStore();

  // Local state
  const [showUploader, setShowUploader] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState<DocumentFolder | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Get family ID from context (would come from auth/family context)
  const familyId = 'current-family-id'; // Replace with actual family context

  // Queries
  const { data: foldersData, isLoading: foldersLoading } = useFolders(familyId);
  const { data: documentsData, isLoading: documentsLoading, refetch: refetchDocuments } = useDocuments(
    familyId,
    {
      folder_id: currentFolderId || undefined,
      ...filters,
      sort_by: sortBy,
      sort_order: sortOrder,
    }
  );
  const { data: statsData } = useDocumentStats(familyId);

  // Mutations
  const createFolderMutation = useCreateFolder();
  const moveFolderMutation = useMoveFolder();
  const deleteFolderMutation = useDeleteFolder();
  const bulkMoveMutation = useBulkMoveDocuments();
  const bulkDeleteMutation = useBulkDeleteDocuments();
  const bulkDownloadMutation = useBulkDownloadDocuments();

  const folders = foldersData?.folders || [];
  const documents = documentsData?.documents || [];
  const selectedDocuments = documents.filter(d => selectedDocumentIds.includes(d.id));

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleFolderNavigate = useCallback((folderId: string | null) => {
    if (folderId) {
      setSearchParams({ folder: folderId });
    } else {
      setSearchParams({});
    }
    clearSelection();
  }, [setSearchParams, clearSelection]);

  const handleFolderCreate = useCallback((parentId?: string) => {
    setEditingFolder(null);
    setShowFolderModal(true);
  }, []);

  const handleFolderEdit = useCallback((folder: DocumentFolder) => {
    setEditingFolder(folder);
    setShowFolderModal(true);
  }, []);

  const handleFolderSubmit = useCallback(async (data: CreateFolderData) => {
    try {
      if (editingFolder) {
        // Update existing folder
        await moveFolderMutation.mutateAsync({
          folderId: editingFolder.id,
          targetFolderId: data.parent_id,
        });
      } else {
        // Create new folder
        await createFolderMutation.mutateAsync({
          familyId,
          data: {
            name: data.name,
            description: data.description,
            color: data.color,
            parent_id: currentFolderId,
          },
        });
      }
      setShowFolderModal(false);
      setEditingFolder(null);
    } catch (error) {
      console.error('Folder operation failed:', error);
    }
  }, [editingFolder, currentFolderId, familyId, createFolderMutation, moveFolderMutation]);

  const handleFolderDelete = useCallback(async (folderId: string) => {
    if (window.confirm('Delete this folder? Documents will be moved to the parent folder.')) {
      try {
        await deleteFolderMutation.mutateAsync(folderId);
        if (currentFolderId === folderId) {
          handleFolderNavigate(null);
        }
      } catch (error) {
        console.error('Delete folder failed:', error);
      }
    }
  }, [currentFolderId, deleteFolderMutation, handleFolderNavigate]);

  const handleDocumentClick = useCallback((document: Document) => {
    setViewingDocument(document);
  }, []);

  const handleDocumentAction = useCallback((action: string, document: Document) => {
    switch (action) {
      case 'view':
        setViewingDocument(document);
        break;
      case 'edit':
        navigate(`/family/documents/${document.id}/edit`);
        break;
      case 'download':
        // Trigger download
        window.open(`${API_BASE_URL}/documents/${document.id}/download`, '_blank');
        break;
      case 'move':
        useDocumentsStore.getState().selectDocument(document.id);
        setShowMoveModal(true);
        break;
      case 'share':
        useDocumentsStore.getState().selectDocument(document.id);
        setShowShareModal(true);
        break;
      case 'delete':
        if (window.confirm(`Delete "${document.title}"?`)) {
          bulkDeleteMutation.mutate({
            familyId,
            documentIds: [document.id],
          });
        }
        break;
    }
  }, [navigate, familyId, bulkDeleteMutation]);

  const handleBulkMove = useCallback(async (targetFolderId: string | null) => {
    try {
      await bulkMoveMutation.mutateAsync({
        familyId,
        documentIds: selectedDocumentIds,
        targetFolderId,
      });
      setShowMoveModal(false);
      clearSelection();
    } catch (error) {
      console.error('Bulk move failed:', error);
    }
  }, [familyId, selectedDocumentIds, bulkMoveMutation, clearSelection]);

  const handleBulkDelete = useCallback(async () => {
    try {
      await bulkDeleteMutation.mutateAsync({
        familyId,
        documentIds: selectedDocumentIds,
      });
      clearSelection();
    } catch (error) {
      console.error('Bulk delete failed:', error);
    }
  }, [familyId, selectedDocumentIds, bulkDeleteMutation, clearSelection]);

  const handleBulkDownload = useCallback(async () => {
    try {
      const result = await bulkDownloadMutation.mutateAsync({
        familyId,
        documentIds: selectedDocumentIds,
      });
      // Download URL returned - open in new tab
      if (result.download_url) {
        window.open(result.download_url, '_blank');
      }
    } catch (error) {
      console.error('Bulk download failed:', error);
    }
  }, [familyId, selectedDocumentIds, bulkDownloadMutation]);

  const handleBulkShare = useCallback(async (documentIds: string[], notes?: string) => {
    // Would call share API
    console.log('Sharing documents:', documentIds, notes);
    setShowShareModal(false);
    clearSelection();
  }, [clearSelection]);

  const handleSort = useCallback((field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field as any);
      setSortOrder('desc');
    }
  }, [sortBy, sortOrder, setSortBy, setSortOrder]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-xl font-semibold text-navy">Document Vault</h1>
              {statsData && (
                <p className="text-sm text-warm-gray">
                  {statsData.total_documents} documents • {statsData.total_size_formatted}
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Search toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  showFilters ? 'bg-sage text-white' : 'hover:bg-cream'
                )}
              >
                <SlidersHorizontal className="w-5 h-5" />
              </button>

              {/* View mode */}
              <div className="flex items-center border border-gray-200 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    'p-2 transition-colors',
                    viewMode === 'grid' ? 'bg-sage text-white' : 'hover:bg-cream'
                  )}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    'p-2 transition-colors',
                    viewMode === 'list' ? 'bg-sage text-white' : 'hover:bg-cream'
                  )}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>

              {/* New folder */}
              <button
                onClick={() => handleFolderCreate()}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-cream transition-colors"
              >
                <FolderPlus className="w-5 h-5" />
                <span className="hidden sm:inline">New Folder</span>
              </button>

              {/* Upload */}
              <button
                onClick={() => setShowUploader(true)}
                className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
              >
                <Upload className="w-5 h-5" />
                <span className="hidden sm:inline">Upload</span>
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="py-4 border-t border-gray-100">
              <DocumentFilters
                filters={filters}
                onFiltersChange={setFilters}
              />
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Folder Tree */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 p-4 sticky top-24">
              <FolderTree
                folders={folders}
                currentFolderId={currentFolderId}
                onFolderSelect={handleFolderNavigate}
                onFolderCreate={handleFolderCreate}
                onFolderEdit={handleFolderEdit}
                onFolderDelete={handleFolderDelete}
                isLoading={foldersLoading}
              />
            </div>
          </aside>

          {/* Main Area */}
          <main className="flex-1 min-w-0">
            {/* Breadcrumb */}
            <div className="mb-4 flex items-center justify-between">
              <FolderBreadcrumb
                folders={folders}
                currentFolderId={currentFolderId}
                onNavigate={handleFolderNavigate}
              />
              <button
                onClick={() => refetchDocuments()}
                className="p-2 hover:bg-cream rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4 text-warm-gray" />
              </button>
            </div>

            {/* Documents Grid/List */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              {viewMode === 'grid' ? (
                <DocumentGrid
                  documents={documents}
                  isLoading={documentsLoading}
                  onDocumentClick={handleDocumentClick}
                  onDocumentAction={handleDocumentAction}
                />
              ) : (
                <DocumentList
                  documents={documents}
                  isLoading={documentsLoading}
                  onDocumentClick={handleDocumentClick}
                  onDocumentAction={handleDocumentAction}
                  onSort={handleSort}
                  sortField={sortBy}
                  sortOrder={sortOrder}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedDocumentIds.length}
        onClearSelection={clearSelection}
        onMove={() => setShowMoveModal(true)}
        onDownload={handleBulkDownload}
        onDelete={handleBulkDelete}
        onShare={() => setShowShareModal(true)}
        isMoving={bulkMoveMutation.isPending}
        isDownloading={bulkDownloadMutation.isPending}
        isDeleting={bulkDeleteMutation.isPending}
      />

      {/* Modals */}
      {showUploader && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6">
              <DocumentUploader
                familyId={familyId}
                folderId={currentFolderId}
                onComplete={() => {
                  setShowUploader(false);
                  refetchDocuments();
                }}
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setShowUploader(false)}
                className="px-4 py-2 text-warm-gray hover:text-navy"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <FolderCreateModal
        isOpen={showFolderModal}
        onClose={() => {
          setShowFolderModal(false);
          setEditingFolder(null);
        }}
        onSubmit={handleFolderSubmit}
        folder={editingFolder}
        isLoading={createFolderMutation.isPending}
      />

      <DocumentMoveModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        onMove={handleBulkMove}
        documents={selectedDocuments}
        folders={folders}
        currentFolderId={currentFolderId}
        isLoading={bulkMoveMutation.isPending}
      />

      <ShareWithAttorneyModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleBulkShare}
        onRevoke={() => {}}
        documents={selectedDocuments}
      />

      {viewingDocument && (
        <DocumentViewer
          document={viewingDocument}
          documents={documents}
          onClose={() => setViewingDocument(null)}
          onNavigate={setViewingDocument}
          onAction={handleDocumentAction}
        />
      )}
    </div>
  );
};

export default DocumentVaultPage;



